import { Link } from '@tanstack/react-router'
import { Film, Image as ImageIcon, Music, Package, PersonStanding, Wand2 } from 'lucide-react'
import { memo, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'

import { annotationKindOf, KIND_LABEL, type AssetCard as AssetCardData } from '@/api/assets'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/utils'
import { formatDuration } from '@/lib/format'
import { ANNOTATE_ROUTE_ID } from '@/routes/annotate.$kind.$uid'

import { StatusRibbon } from './StatusBadge'

/**
 * One asset, as a big warm card (§8: the library is browsed, not audited).
 *
 * The card is a **fixed slot model** — thumb, ribbon, name, meta row, action
 * — so 200 of them line up no matter how long a slug is. Text is clamped
 * and the full name is in `title`; nothing reflows because one background
 * is called `forest_behind_tree_live` and its neighbour is called `pond`.
 *
 * Selection is owned by the grid (roving tabindex lives there); this
 * component reports intent and renders state.
 */

export interface AssetCardProps {
  asset: AssetCardData
  selected: boolean
  /** The card that currently holds `tabIndex=0` in the roving grid. */
  focused: boolean
  onSelect(uid: string, mode: 'replace' | 'toggle' | 'range'): void
  onFocus?(uid: string): void
  /** Grid cells are squares by default; the list view uses its own row. */
  className?: string
}

/** Card geometry the grid uses to estimate row heights: a 4:3 thumb over a
 *  fixed 96 px body (`h-24`), plus the 1 px card border top and bottom. */
export const CARD_THUMB_ASPECT = 3 / 4
export const CARD_BODY_HEIGHT = 96
export const CARD_BORDER = 2

const KIND_ICON: Record<AssetCardData['kind'], ReactNode> = {
  background: <ImageIcon aria-hidden />,
  live_background: <Film aria-hidden />,
  character: <PersonStanding aria-hidden />,
  object: <Package aria-hidden />,
  audio: <Music aria-hidden />,
}

/** Modifier keys → selection mode, the convention every file browser uses. */
export function selectionMode(e: MouseEvent | KeyboardEvent): 'replace' | 'toggle' | 'range' {
  if (e.shiftKey) return 'range'
  if (e.ctrlKey || e.metaKey) return 'toggle'
  return 'replace'
}

export const AssetCard = memo(function AssetCard({ asset, selected, focused, onSelect, onFocus, className }: AssetCardProps) {
  // Only stages have a hand editor now; characters and objects are puppets (docs/SKEL-STORY.md P7).
  const annotateKind = annotationKindOf(asset.kind) === 'stage' ? 'stage' : null
  const thumb = asset.media?.thumb
  const isAudio = asset.kind === 'audio'

  return (
    <div
      role="option"
      aria-selected={selected}
      aria-label={asset.name}
      data-uid={asset.uid}
      data-testid="asset-card"
      tabIndex={focused ? 0 : -1}
      onFocus={() => onFocus?.(asset.uid)}
      onClick={(e) => onSelect(asset.uid, selectionMode(e))}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(asset.uid, e.key === ' ' ? 'toggle' : selectionMode(e))
        }
      }}
      className={cn(
        'group card relative flex h-full cursor-pointer flex-col overflow-hidden text-left transition-shadow',
        'focus-visible:focus-ring hover:shadow-pop',
        selected && 'ring-2 ring-accent',
        asset.hidden && 'opacity-55',
        className,
      )}
    >
      {/* Thumb ------------------------------------------------------------ */}
      <div className={cn('relative aspect-[4/3] w-full overflow-hidden', isAudio ? 'bg-paper-2' : 'bg-checker')}>
        {thumb && !isAudio ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            decoding="async"
            draggable={false}
            className="size-full object-contain"
            width={asset.width}
            height={asset.height}
          />
        ) : (
          <span className="flex size-full items-center justify-center text-ink-3 [&_svg]:size-10">{KIND_ICON[asset.kind]}</span>
        )}

        <span className="pointer-events-none absolute left-2 top-2">
          <StatusRibbon review={asset.review} assetStatus={asset.status} />
        </span>

        <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-paper/90 px-2 py-0.5 text-xs font-medium text-ink-2 [&_svg]:size-3.5">
          {KIND_ICON[asset.kind]}
          {KIND_LABEL[asset.kind]}
        </span>

        {/* Annotate — an overlay on the picture, never a layout row. It used to
            sit below the body with `opacity-0`, invisible but still ~48 px tall
            on every non-audio card, so cards had two heights and the grid's
            rows overlapped. Hover/keyboard focus reveal it; on touch screens
            (no hover) it is always shown. Hidden for audio (§3.2). */}
        {annotateKind ? (
          <div
            className={cn(
              'absolute bottom-2 right-2 opacity-0 transition-opacity duration-150',
              'group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100',
              'motion-reduce:transition-none',
            )}
          >
            <Button asChild size="sm" variant="secondary" className="shadow-pop" onClick={(e) => e.stopPropagation()}>
              <Link to={ANNOTATE_ROUTE_ID} params={{ kind: annotateKind, _splat: asset.uid }} aria-label={`Annotate ${asset.name}`}>
                <Wand2 aria-hidden />
                Annotate
              </Link>
            </Button>
          </div>
        ) : null}
      </div>

      {/* Body — fixed slots with a fixed height, so every card in the grid is
          exactly the same height whatever its kind or metadata. -------------- */}
      <div className="flex h-24 min-w-0 flex-none flex-col gap-1 p-3">
        <p className="truncate text-sm font-semibold text-ink" title={asset.name}>
          {asset.name}
        </p>
        <p className="truncate text-xs text-ink-3" title={asset.uid}>
          {asset.category ?? asset.group ?? asset.world ?? asset.uid}
        </p>

        <div className="mt-auto flex h-6 flex-nowrap items-center gap-x-2 overflow-hidden whitespace-nowrap pt-1 text-xs text-ink-2">
          {asset.clip_count != null ? (
            <span>
              {asset.clip_count} clip{asset.clip_count === 1 ? '' : 's'}
              {asset.broken_clip_count ? <span className="text-danger"> · {asset.broken_clip_count} broken</span> : null}
            </span>
          ) : null}
          {asset.skin_count ? <span>{asset.skin_count} skins</span> : null}
          {asset.skin && asset.skin_verified === false ? <span className="text-warn">unverified skin</span> : null}
          {asset.duration_s != null ? <span>{formatDuration(asset.duration_s)}</span> : null}
          {asset.width && asset.height && !isAudio ? (
            <span className="text-ink-3">
              {asset.width}×{asset.height}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  )
})
